






























































































import {
  ref,
  defineComponent,
  toRefs,
  reactive,
  inject,
  computed,
} from '@vue/composition-api'

import { importMediaFromZip } from '@/shared/api'
import { alertSymbol } from '@/shared/state-management/symbols'
import CloseButton from '@/components/custom/CloseButton.vue'

const maxSize = 1024 * 1024 * 1024
export default defineComponent({
  name: 'ZipMediaImport',
  components: {
    CloseButton,
  },
  props: {
    textbook: Object,
  },
  setup(props, { root, emit }) {
    const MediaUploadForm = ref(null)
    const data = reactive({
      valid: true,
      file: null,
    })

    const dragover = ref(false)

    const progressData = ref({ loaded: 0, total: null })
    const uploadStartedTime = ref(null)
    const uploadFinishedTime = ref(null)
    const timerInterval = ref(null)
    const currentTime = ref(null)

    const $alert = inject(alertSymbol) as Function

    const rules = reactive({
      fileRules: [
        () => !!data.file || root.$t('media.zipImport.fileRequired'),
        () =>
          (!!data.file && data.file.type.includes('zip')) ||
          root.$t('media.zipImport.noZip'),
        () =>
          (!!data.file && data.file.size < maxSize) ||
          root.$t('media.zipImport.fileTooLarge', {
            maxSize: (maxSize / (1024 * 1024 * 1024)).toFixed(2),
          }),
      ],
    })

    const loadingData = ref(false)
    const onUploadProgress = (progressEvent: ProgressEvent<EventTarget>) => {
      if (
        uploadFinishedTime.value === null &&
        progressEvent.loaded === progressEvent.total
      ) {
        uploadFinishedTime.value = new Date()
      }
      progressData.value = {
        loaded: progressEvent.loaded,
        total: progressEvent.total,
      }
    }
    const addDropFile = (e: DragEvent) => {
      dragover.value = false
      // to support drag and drop https://github.com/vuetifyjs/vuetify/issues/7836#issuecomment-582841222
      data.file = e.dataTransfer.files?.length ? e.dataTransfer.files[0] : null
    }

    const startTimer = () => {
      uploadStartedTime.value = new Date()
      timerInterval.value = window.setInterval(() => {
        currentTime.value = new Date()
      }, 500)
    }
    const stopTimer = () => {
      if (timerInterval.value !== null) {
        clearInterval(timerInterval.value)
      }
      timerInterval.value = null
      uploadStartedTime.value = null
    }

    const formatSeconds = (seconds: number) => {
      const minutes = Math.floor(seconds / 60)
      const hours = Math.floor(minutes / 60)
      if (minutes === 0) {
        return `${seconds}s`
      }
      if (hours === 0) {
        return `${minutes}m ${seconds % 60}s`
      }
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`
    }

    const uploadProgressTime = computed(() => {
      if (!currentTime.value) {
        return '0s'
      }
      const referenceTime = uploadFinishedTime.value
        ? uploadFinishedTime.value
        : currentTime.value
      return formatSeconds(
        Math.floor(
          (referenceTime.getTime() - uploadStartedTime.value.getTime()) / 1000
        )
      )
    })

    const processingProgressTime = computed(() => {
      if (!uploadFinishedTime.value) {
        return '0s'
      }
      return formatSeconds(
        Math.floor(
          (currentTime.value.getTime() - uploadFinishedTime.value.getTime()) /
            1000
        )
      )
    })

    const submit = async (event: Event) => {
      event.preventDefault()
      event.stopPropagation()
      if (loadingData.value) {
        return
      }
      const form = MediaUploadForm.value
      if (!form.validate()) {
        return
      }

      loadingData.value = true
      startTimer()

      try {
        const createdIds = await importMediaFromZip(
          props.textbook.id,
          data.file,
          onUploadProgress
        )

        $alert({
          text: root.$t('media.zipImport.success', {
            number: createdIds.length,
          }),
          type: 'success',
        })
        emit('media-imported')
        loadingData.value = false

        stopTimer()
      } catch (error) {
        loadingData.value = false
        stopTimer()
        throw error
      }
    }

    return {
      ...toRefs(data),
      ...toRefs(rules),
      loadingData,
      addDropFile,
      MediaUploadForm,
      submit,
      dragover,
      progressData,
      uploadProgressTime,
      processingProgressTime,
    }
  },
})
